import React, { useEffect, useMemo, useState } from "react";
import {
  FaChalkboardTeacher,
  FaExclamationTriangle,
  FaTimes,
  FaUserTie,
  FaUsers,
} from "react-icons/fa";

import Reveal from "../../../components/Reveal/Reveal";
import { getAllTeamMembers } from "../../../services/teamServices";

const BASE_URL = process.env.REACT_APP_API_URL || "http://localhost:3317";

const isExternalURL = (url) => {
  try {
    const u = new URL(url);
    return u.protocol === "http:" || u.protocol === "https:";
  } catch {
    return false;
  }
};

const getImageUrl = (url) => {
  if (!url) return "";

  const cleanUrl = String(url).trim();

  if (!cleanUrl) return "";

  if (
    isExternalURL(cleanUrl) ||
    cleanUrl.startsWith("data:") ||
    cleanUrl.startsWith("blob:")
  ) {
    return cleanUrl;
  }

  const cleanBaseUrl = BASE_URL.replace(/\/$/, "");

  if (cleanUrl.startsWith("/")) {
    return `${cleanBaseUrl}${cleanUrl}`;
  }

  return `${cleanBaseUrl}/${cleanUrl}`;
};

const normalizeCat = (value) =>
  String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim()
    .toLowerCase();

const getMemberPhoto = (member) =>
  getImageUrl(
    member?.photoURL ||
      member?.photo_url ||
      member?.photo ||
      member?.image_url ||
      member?.avatar
  );

const getMemberName = (member) =>
  `${member?.prenom || member?.first_name || ""} ${
    member?.nom || member?.last_name || ""
  }`.trim() ||
  member?.name ||
  "Membre du club";

const isActiveMember = (member) => {
  if (
    member?.is_active === undefined ||
    member?.is_active === null ||
    member?.is_active === ""
  ) {
    return true;
  }

  return (
    member.is_active === true ||
    member.is_active === 1 ||
    member.is_active === "1"
  );
};

const useLockBody = (locked) => {
  useEffect(() => {
    if (!locked) return undefined;

    const previousOverflow = document.body.style.overflow;

    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [locked]);
};

const SkeletonCard = () => (
  <div className="overflow-hidden rounded-[2rem] border border-slate-200 bg-white p-5 shadow-sm dark:border-white/10 dark:bg-slate-900">
    <div className="flex items-center gap-4">
      <div className="h-24 w-24 animate-pulse rounded-3xl bg-slate-200 dark:bg-slate-800" />
      <div className="flex-1 space-y-3">
        <div className="h-5 w-2/3 animate-pulse rounded-full bg-slate-200 dark:bg-slate-800" />
        <div className="h-4 w-1/2 animate-pulse rounded-full bg-slate-200 dark:bg-slate-800" />
      </div>
    </div>
  </div>
);

const EmptyState = ({ text }) => (
  <div className="rounded-[2rem] border border-dashed border-slate-300 bg-white/75 p-8 text-center shadow-sm dark:border-white/10 dark:bg-white/5">
    <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-orange-100 text-primary dark:bg-orange-500/10">
      <FaUsers />
    </div>

    <p className="text-sm font-semibold text-slate-500 dark:text-slate-400">
      {text}
    </p>
  </div>
);

const ErrorBanner = ({ message }) => (
  <div className="mb-8 rounded-[1.5rem] border border-red-200 bg-red-50 p-4 text-sm font-semibold text-red-700 dark:border-red-500/20 dark:bg-red-500/10 dark:text-red-300">
    <div className="flex items-center justify-center gap-3">
      <FaExclamationTriangle />
      {message}
    </div>
  </div>
);

const ImageModal = ({ image, onClose }) => {
  useLockBody(Boolean(image?.src));

  useEffect(() => {
    if (!image?.src) return undefined;

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        onClose();
      }
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [image, onClose]);

  if (!image?.src) return null;

  return (
    <div
      className="fixed inset-0 z-[120] flex items-center justify-center bg-slate-950/85 p-4 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      aria-label="Photo agrandie"
      onClick={onClose}
    >
      <div
        className="relative max-h-[92vh] max-w-4xl overflow-hidden rounded-[2rem] border border-white/10 bg-white/10 p-2 shadow-2xl backdrop-blur-xl"
        onClick={(event) => event.stopPropagation()}
      >
        <img
          src={image.src}
          alt={image.alt || "Photo du membre"}
          className="max-h-[86vh] w-full rounded-[1.5rem] object-contain"
        />

        <button
          type="button"
          onClick={onClose}
          className="absolute right-4 top-4 flex h-11 w-11 items-center justify-center rounded-full bg-white text-slate-950 shadow-lg transition hover:scale-105"
          aria-label="Fermer"
        >
          <FaTimes />
        </button>
      </div>
    </div>
  );
};

const MemberCard = ({ member, accent = "orange", onOpenImage }) => {
  const name = getMemberName(member);
  const src = getMemberPhoto(member);
  const fonction = member?.fonction || member?.role || "Membre du club";

  const accentClasses =
    accent === "green"
      ? "from-emerald-500 to-teal-400"
      : "from-orange-500 to-amber-400";

  return (
    <article className="group relative h-full overflow-hidden rounded-[2rem] border border-slate-200 bg-white p-5 shadow-sm transition duration-300 hover:-translate-y-2 hover:shadow-2xl dark:border-white/10 dark:bg-slate-900">
      <div
        className={`absolute inset-x-0 top-0 h-1.5 bg-gradient-to-r ${accentClasses}`}
      />

      <div className="flex flex-col gap-5 sm:flex-row sm:items-center">
        <button
          type="button"
          onClick={() =>
            src &&
            onOpenImage({
              src,
              alt: name,
            })
          }
          className="relative mx-auto h-28 w-28 shrink-0 overflow-hidden rounded-[1.7rem] bg-slate-100 shadow-lg ring-1 ring-slate-200 transition group-hover:-translate-y-1 dark:bg-slate-800 dark:ring-white/10 sm:mx-0"
          aria-label={`Agrandir la photo de ${name}`}
        >
          {src ? (
            <img
              src={src}
              alt={name}
              loading="lazy"
              className="h-full w-full object-cover transition duration-700 group-hover:scale-110"
              onError={(event) => {
                event.currentTarget.style.display = "none";
              }}
            />
          ) : (
            <div className="flex h-full w-full items-center justify-center bg-gradient-to-br from-slate-800 to-slate-950 text-3xl font-bold text-white">
              {name.charAt(0).toUpperCase()}
            </div>
          )}
        </button>

        <div className="min-w-0 text-center sm:text-left">
          <h4 className="font-barlow text-2xl font-bold leading-tight text-slate-950 dark:text-white">
            {name}
          </h4>

          <p className="mt-2 text-sm font-semibold leading-6 text-slate-500 dark:text-slate-400">
            {fonction}
          </p>

          <div className="mt-4 inline-flex rounded-full bg-slate-100 px-4 py-2 text-xs font-bold uppercase tracking-[0.16em] text-slate-500 dark:bg-white/10 dark:text-slate-300">
            {normalizeCat(member?.categorie) === "bureau"
              ? "Bureau"
              : "Enseignant"}
          </div>
        </div>
      </div>
    </article>
  );
};

const TeamSection = ({
  title,
  subtitle,
  icon: Icon,
  members,
  loading,
  emptyText,
  accent,
}) => (
  <section className="mt-14">
    <Reveal className="mb-7 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
      <div>
        <div className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white/80 px-4 py-2 text-xs font-bold uppercase tracking-[0.2em] text-slate-500 shadow-sm dark:border-white/10 dark:bg-white/5 dark:text-slate-300">
          <Icon className="text-primary" />
          {subtitle}
        </div>

        <h3 className="mt-4 font-barlow text-3xl font-bold text-slate-950 dark:text-white sm:text-4xl">
          {title}
        </h3>
      </div>

      <p className="text-sm font-semibold text-slate-400">
        {members.length} membre{members.length > 1 ? "s" : ""}
      </p>
    </Reveal>

    <div className="grid grid-cols-1 gap-6 md:grid-cols-2 xl:grid-cols-3">
      {loading &&
        Array.from({ length: 3 }).map((_, index) => (
          <SkeletonCard key={`team-skeleton-${title}-${index}`} />
        ))}

      {!loading && members.length === 0 && <EmptyState text={emptyText} />}

      {!loading &&
        members.map((member, index) => (
          <Reveal
            key={member.id || `${getMemberName(member)}-${index}`}
            variant="zoom"
            delay={index * 80}
          >
            <MemberCard
              member={member}
              accent={accent}
              onOpenImage={members.onOpenImage}
            />
          </Reveal>
        ))}
    </div>
  </section>
);

export default function Team() {
  const [bureauMembers, setBureauMembers] = useState([]);
  const [enseignants, setEnseignants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState("");
  const [openImage, setOpenImage] = useState(null);

  useEffect(() => {
    let mounted = true;

    const fetchTeamMembers = async () => {
      try {
        setLoading(true);
        setErrorMsg("");

        const teamMembers = await getAllTeamMembers();

        if (!mounted) return;

        const activeMembers = (Array.isArray(teamMembers) ? teamMembers : [])
          .filter(isActiveMember)
          .sort((a, b) => {
            const orderA = Number(a?.ordre || a?.order || 999);
            const orderB = Number(b?.ordre || b?.order || 999);

            return orderA - orderB;
          });

        const bureau = activeMembers.filter(
          (member) => normalizeCat(member?.categorie) === "bureau"
        );

        const teachers = activeMembers.filter((member) =>
          normalizeCat(member?.categorie).startsWith("enseign")
        );

        setBureauMembers(bureau);
        setEnseignants(teachers);
      } catch (error) {
        console.error("Erreur lors du chargement de l’équipe :", error);

        if (mounted) {
          setErrorMsg("Impossible de charger l’équipe du club.");
        }
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    fetchTeamMembers();

    return () => {
      mounted = false;
    };
  }, []);

  const bureauWithImageHandler = useMemo(() => {
    const items = [...bureauMembers];
    items.onOpenImage = setOpenImage;
    return items;
  }, [bureauMembers]);

  const enseignantsWithImageHandler = useMemo(() => {
    const items = [...enseignants];
    items.onOpenImage = setOpenImage;
    return items;
  }, [enseignants]);

  return (
    <section className="relative overflow-hidden bg-slate-50 py-20 text-slate-950 dark:bg-slate-950 dark:text-white lg:py-28">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(242,113,49,0.12),transparent_34%),radial-gradient(circle_at_bottom_right,rgba(59,130,246,0.10),transparent_32%)]" />
      <div className="absolute inset-0 bg-[linear-gradient(to_right,rgba(148,163,184,0.13)_1px,transparent_1px),linear-gradient(to_bottom,rgba(148,163,184,0.13)_1px,transparent_1px)] bg-[size:42px_42px] opacity-35 dark:opacity-15" />

      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <Reveal className="mx-auto max-w-3xl text-center">
          <div className="inline-flex items-center gap-2 rounded-full border border-orange-200 bg-orange-50 px-4 py-2 text-xs font-bold uppercase tracking-[0.22em] text-orange-700 shadow-sm dark:border-orange-500/20 dark:bg-orange-500/10 dark:text-orange-300">
            <FaUsers />
            L’équipe du club
          </div>

          <h2 className="mt-6 font-barlow text-4xl font-bold tracking-tight text-slate-950 dark:text-white sm:text-5xl lg:text-6xl">
            Notre{" "}
            <span className="text-primary">
              équipe
            </span>
          </h2>

          <p className="mx-auto mt-5 max-w-2xl text-base leading-8 text-slate-600 dark:text-slate-300">
            Dirigeants, bénévoles et enseignants accompagnent les adhérents au
            quotidien et font vivre le club tout au long de la saison.
          </p>
        </Reveal>

        {errorMsg && <ErrorBanner message={errorMsg} />}

        <TeamSection
          title="Le bureau"
          subtitle="Administration"
          icon={FaUserTie}
          members={bureauWithImageHandler}
          loading={loading}
          emptyText="Aucun membre du bureau pour le moment."
          accent="orange"
        />

        <TeamSection
          title="Les enseignants"
          subtitle="Encadrement"
          icon={FaChalkboardTeacher}
          members={enseignantsWithImageHandler}
          loading={loading}
          emptyText="Aucun enseignant trouvé pour le moment."
          accent="green"
        />
      </div>

      <ImageModal image={openImage} onClose={() => setOpenImage(null)} />
    </section>
  );
}